import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMySurveys } from '../../features/mySurveys/mySurveysActions';
import { resetMySurveysState } from '../../features/mySurveys/mySurveysSlice';

const MySurveys = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { canteen } = location.state || {};

    // Date will not be passed by default
    const [reportingDate, setReportingDate] = useState('');

    const { loading, error, surveyData } = useSelector((state) => state.mySurveys);

    useEffect(() => {
        if (canteen) {
            // 1 = kiosk, 2 = kitchen, 3 = kitchen cum dining
            const cType = canteen.canteen_type || canteen.canteen_type_id;
            dispatch(fetchMySurveys(cType, canteen.id, reportingDate));
        }
    }, [dispatch, canteen, reportingDate]);

    useEffect(() => {
        return () => {
            dispatch(resetMySurveysState());
        };
    }, [dispatch]);

    if (!canteen) {
        return (
            <div style={{ padding: '20px' }}>
                <p>No canteen details found. Please go back and select a canteen.</p>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
    
    {/* Canteen Details */}
    <div className="lg:col-span-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 sm:p-6 shadow-sm">
        <h1 className="mb-4 text-sm font-bold tracking-tight text-[var(--color-primary)]">
            Canteen Details
        </h1>

        <div className="space-y-2">
            <div className="flex items-start justify-between gap-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2.5">
                <span className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                    ID
                </span>

                <span className="text-sm font-medium text-right text-[var(--color-text)] break-all">
                    {canteen.id}
                </span>
            </div>

            <div className="flex items-start justify-between gap-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2.5">
                <span className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                    Name/Area
                </span>

                <span className="text-sm font-medium text-right text-[var(--color-text)] break-words max-w-[65%] leading-snug">
                    {canteen.name || canteen.area_name}
                </span>
            </div>

            <div className="flex items-start justify-between gap-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2.5">
                <span className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                    Type
                </span>

                <span className="text-sm font-medium text-right text-[var(--color-text)] break-words max-w-[65%] leading-snug">
                    {canteen.canteen_type_name || canteen.type_name || canteen.canteen_type}
                </span>
            </div>
        </div>
    </div>

    {/* Date Filter */}
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 sm:p-6 shadow-sm">
        <h2 className="mb-4 text-sm font-bold tracking-tight text-[var(--color-primary)]">
            Survey Filter
        </h2>

        <div className="flex flex-col gap-2">
            <label
                htmlFor="reportingDate"
                className="text-sm font-medium text-[var(--color-text-muted)]"
            >
                Reporting Date
            </label>

            <input
                type="date"
                id="reportingDate"
                value={reportingDate}
                onChange={(e) => setReportingDate(e.target.value)}
                className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]"
            />
        </div>
    </div>
</div>
            {loading && <p>Loading survey data...</p>}

            {
                error && (
                    <div style={{ color: 'red', padding: '10px', background: '#ffebee', borderRadius: '4px', marginBottom: '20px' }}>
                        Error: {error}
                    </div>
                )
            }

            {!loading && !error && (surveyData?.survey_list || surveyData?.data?.survey_list) && (
                <div>
                    <p className='text-sm font-semibold tracking-tight text-[var(--color-text)] sm:text-sm my-4 text-center text-green-500'>Survey fetched successfully</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
                        {(surveyData?.survey_list || surveyData?.data?.survey_list || []).map((survey) => {
                            const isCompleted = survey.final.is_total_survey_complete === 1;
                            const basicInfo = survey.basic_info || {};

                            return (
                                <div
                                    key={survey.id}
                                    className="flex flex-col rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
                                >
                                    {/* Header */}
                                    <div className="bg-[var(--color-primary)] px-5 py-4">
                                        <h3 className="text-lg font-bold text-white">
                                            Survey #{survey.id}
                                        </h3>

                                        <p className="text-sm text-orange-100 mt-1">
                                            {basicInfo.reporting_date || 'No Reporting Date'}
                                        </p>
                                    </div>

                                    {/* Body */}
                                    <div className="flex-1 p-5 space-y-3">
                                        {Object.entries(basicInfo)
                                            .filter(
                                                ([key]) =>
                                                    ![
                                                        'lattitude_longitude',
                                                        'user_id',
                                                        'canteens_id'
                                                    ].includes(key)
                                            )
                                            .map(([key, value]) => (
                                                <div
                                                    key={key}
                                                    className="flex items-start justify-between gap-4 border-b border-[var(--color-border)] pb-2"
                                                >
                                                    <span className="text-sm font-semibold text-[var(--color-text-muted)] capitalize">
                                                        {key
                                                            .replace(/_/g, ' ')
                                                            .replace(/\b\w/g, l => l.toUpperCase())}
                                                    </span>

                                                    <span className="text-sm text-[var(--color-text)] text-right font-medium">
                                                        {String(value || '-')}
                                                    </span>
                                                </div>
                                            ))}

                                        {/* Status */}
                                        <div className="pt-2">
                                            <span
                                                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold
                                                ${isCompleted
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-orange-100 text-orange-700'
                                                    }`}
                                            >
                                                {isCompleted ? 'Survey Completed' : 'Survey Pending'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div className="p-5 pt-0">
                                        <button
                                            className={`w-full rounded-xl px-4 py-3 text-sm font-bold text-white transition-all duration-300
                                                ${isCompleted
                                                    ? 'bg-[var(--color-info)] hover:opacity-90'
                                                    : 'bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)]'
                                                }`}
                                            onClick={() => {
                                                const cType = canteen.canteen_type || canteen.canteen_type_id;
                                                let surveyTypeStr = '';
                                                if (String(cType) === '1') surveyTypeStr = 'kiosk';
                                                else if (String(cType) === '2') surveyTypeStr = 'kitchen';
                                                else if (String(cType) === '3') surveyTypeStr = 'kitchen-cum-dining';

                                                navigate(`/survey/${surveyTypeStr}`, {
                                                    state: {
                                                        from: location.pathname,
                                                        canteen: canteen,
                                                        districtId: canteen.district_id,
                                                        municipalityId: canteen.municipality_id,
                                                        wardId: canteen.ward_id,
                                                        existingSurveyData: survey
                                                    }
                                                });
                                            }}
                                        >
                                            {isCompleted ? 'Update Survey' : 'Complete Survey'}
                                        </button>
                                    </div>
                                </div>

                            );
                        })}
                    </div>
                </div>
            )
            }

            {!loading && !error && surveyData && (surveyData?.survey_list?.length === 0 || surveyData?.data?.survey_list?.length === 0) && (
                <p className="text-center text-[var(--color-text-muted)] py-4">
                    No survey data available for this selection/date.
                </p>
            )}

            {!loading && !error && surveyData && !surveyData?.survey_list && !surveyData?.data?.survey_list && (
                <p className="text-center text-[var(--color-text-muted)] py-4">
                    No survey list available in the response.
                </p>
            )}
        </div >
    );
};

export default MySurveys;
